import { InfoPopover, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { CapsuleTrend } from '@/components';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { TrendPill } from '@/components/Pill/ConservationPill';
import { getDisplayNumber, useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useTrendLabelFromPlotFact } from '@/hooks/useTrendLabelFromFact';
import { isNullish } from '@/utils/booleans';

export const HabitatIntactnessThreatsBreakdownStatsTile = ({
  latestFact,
  baselineFact,
  trendFact,
}: {
  latestFact: PlotReportFactElement<number | null> | undefined;
  baselineFact: PlotReportFactElement<number | null> | undefined;
  trendFact: PlotReportFactElement<number | null> | undefined;
}) => {
  const { t } = useTranslation();

  const trendLabel = useTrendLabelFromPlotFact({
    fact: IndicatorName.threat_breakdown_total_previous_5y_change,
  });
  const trendUnit = trendFact ? t(`global.ui.unit.${trendFact.unit ?? UnitEnum['%']}`) : null;

  const latestAnalysisDate = printLatestDate([latestFact?.measured_at]);
  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  const baselineMeasuredAtDate = printLatestDate([baselineFact?.measured_at]);

  let threatsBreakdownTrendDisplay = `${useDisplayNumber(Number(trendFact?.value ?? 0), {
    signDisplay: 'always',
  })} ${trendUnit}`;
  threatsBreakdownTrendDisplay = !isNullish(trendFact?.value) ? threatsBreakdownTrendDisplay : '-';

  const explainerText =
    trendLabel && trendFact?.value !== null && trendFact?.value !== undefined
      ? t(
          `shared.ncaDetail.details.habitatIntactness.threatsBreakdown.explainers.whatDoesThisMean.body.trendLabel.${trendLabel}`,
          '',
          {
            value: `${getDisplayNumber(trendFact.value, undefined, {
              signDisplay: 'always',
            })} ${trendUnit}`,
          },
        )
      : null;

  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4' data-testid='habitat-intactness-threats-breakdown-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.stats.header.title')}
          supplementaryInfo={
            <Stack direction='row' spacing={2} className='w-full justify-end'>
              <span>{`${latestAnalysisTranslation} ${latestAnalysisDate}`}</span>
              <InfoPopover
                position='top'
                body={t('shared.ncaDetail.details.habitatIntactness.labels.missingDataContext')}
              />
            </Stack>
          }
          popover={{
            title: t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.stats.header.title'),
            body: t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.stats.header.tooltip'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-16'>
          {latestFact && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.latestAnalysis')}
              fact={latestFact}
              capsuleIconProps={{
                icon: getNCChangeIcon(latestFact),
                variant: getNCChangeIconVariant(latestFact),
              }}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
                body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', {
                  date: latestAnalysisDate,
                }),
                popoverTriggerProps: {
                  'data-testid': 'habitat-intactness-threats-breakdown-latest-analysis-popover',
                },
              }}
              trend={
                typeof trendFact?.value === 'number' && (
                  <CapsuleTrend variant={trendFact.value <= 0 ? 'positive' : 'negative'}>
                    {threatsBreakdownTrendDisplay}
                  </CapsuleTrend>
                )
              }
            />
          )}
          {baselineFact && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.explainers.baseline.title')}
              capsuleIconProps={{
                icon: getNCChangeIcon(baselineFact),
                variant: getNCChangeIconVariant(baselineFact),
              }}
              fact={baselineFact}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.explainers.baseline.title'),
                body: t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.explainers.baseline.body', {
                  date: baselineMeasuredAtDate,
                }),
              }}
            />
          )}
        </Stack>
        {explainerText && (
          <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
            <Stack direction='row' spacing={4}>
              {trendLabel && <TrendPill trendLabel={trendLabel} />}
            </Stack>
            <span className='typography-body1Bold'>
              {t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.explainers.whatDoesThisMean.title')}
            </span>
            <p className='typography-body1'>{explainerText}</p>
          </Stack>
        )}
      </div>
    </Stack>
  );
};
